import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'

interface ProviderGroup {
  id: string
  name: string
  displayName: string
  description?: string
  memberCount: number
  groupType?: string
}

interface GroupsPage {
  data: ProviderGroup[]
  nextCursor: string | null
  hasMore: boolean
  totalEstimate?: number
}

type RawGroupsResponse =
  | GroupsPage
  | ProviderGroup[]
  | {
      data?: ProviderGroup[] | GroupsPage
      nextCursor?: string | null
      hasMore?: boolean
      totalEstimate?: number
    }

function normalizeGroupsPage(raw: RawGroupsResponse): GroupsPage {
  if (Array.isArray(raw)) {
    return {
      data: raw,
      nextCursor: null,
      hasMore: false,
      totalEstimate: raw.length,
    }
  }

  const nestedData = raw?.data

  if (Array.isArray(nestedData)) {
    return {
      data: nestedData,
      nextCursor: raw.nextCursor ?? null,
      hasMore: raw.hasMore ?? false,
      totalEstimate: raw.totalEstimate ?? nestedData.length,
    }
  }

  if (nestedData && typeof nestedData === 'object' && Array.isArray(nestedData.data)) {
    return {
      data: nestedData.data,
      nextCursor: nestedData.nextCursor ?? null,
      hasMore: nestedData.hasMore ?? false,
      totalEstimate: nestedData.totalEstimate ?? nestedData.data.length,
    }
  }

  return {
    data: [],
    nextCursor: null,
    hasMore: false,
    totalEstimate: 0,
  }
}

export function useProviderGroups(provider: string, search: string = '', enabled: boolean = true) {
  return useInfiniteQuery({
    queryKey: ['provider-groups', provider, search],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const params = new URLSearchParams()
      params.set('limit', '50')
      if (pageParam) params.set('cursor', pageParam)
      if (search) params.set('search', search)
      const raw = await fetchApi<RawGroupsResponse>(`/integrations/${provider}/groups?${params.toString()}`)
      return normalizeGroupsPage(raw)
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    enabled,
  })
}
